import { useNavigate } from 'react-router-dom';
import SeoHead from '../../components/SeoHead';

const sections = [
  {
    id: 1,
    icon: 'ri-vip-crown-line',
    color: 'purple',
    title: 'Formalidad, tradición y protocolo',
    content: [
      {
        subtitle: 'Una cultura de alta formalidad',
        text: 'Austria tiene una de las culturas laborales más formales de Europa. Los títulos académicos se usan con frecuencia en la comunicación profesional: "Herr Doktor", "Frau Magistra". El tratamiento formal (Sie) se mantiene hasta que el colega de mayor rango propone el cambio. Esta formalidad no es frialdad; es respeto y protocolo.',
      },
      {
        subtitle: 'La herencia imperial en la cultura empresarial',
        text: 'Viena fue durante siglos el centro del Imperio Austro-Húngaro, y esa herencia se refleja en la cultura empresarial: elegancia, protocolo, respeto por la jerarquía y un sentido profundo de la tradición. Las empresas austriacas valoran la estabilidad, la continuidad y el rigor por encima de la disrupción y la innovación rápida.',
      },
      {
        subtitle: 'Puntualidad y fiabilidad',
        text: 'Como en Alemania, la puntualidad es innegociable en Austria. Llegar tarde a una reunión o entrevista es una señal de falta de respeto. La fiabilidad y el cumplimiento de compromisos son valores fundamentales. Si dices que harás algo, se espera que lo hagas. La palabra dada tiene mucho peso en la cultura austriaca.',
      },
    ],
  },
  {
    id: 2,
    icon: 'ri-building-4-line',
    color: 'amber',
    title: 'Estructura jerárquica y toma de decisiones',
    content: [
      {
        subtitle: 'Jerarquía clara y respetada',
        text: 'Las empresas austriacas tienen estructuras jerárquicas bien definidas y respetadas. Las decisiones importantes se toman en los niveles superiores y se comunican hacia abajo. No es habitual que los empleados de niveles medios cuestionen abiertamente las decisiones de la dirección, aunque sí pueden expresar su opinión en los canales adecuados.',
      },
      {
        subtitle: 'Proceso de decisión deliberado',
        text: 'Las decisiones en Austria se toman de forma cuidadosa y deliberada. No se improvisa. Se analizan todas las opciones, se consulta a los expertos internos y se documenta el proceso. Esto puede parecer lento, pero garantiza que las decisiones sean sólidas y bien fundamentadas. La prisa en la toma de decisiones genera desconfianza.',
      },
      {
        subtitle: 'Respeto por la experiencia y la antigüedad',
        text: 'En Austria, la experiencia y la antigüedad en la empresa son muy valoradas. Los profesionales con muchos años en la organización tienen una autoridad informal importante. Respeta ese conocimiento acumulado y aprende de él. La arrogancia de los recién llegados, por muy cualificados que sean, no es bien recibida.',
      },
    ],
  },
  {
    id: 3,
    icon: 'ri-speak-line',
    color: 'emerald',
    title: 'Comunicación y relaciones profesionales',
    content: [
      {
        subtitle: 'Comunicación indirecta y diplomática',
        text: 'A diferencia de Alemania, los austriacos tienden a ser más indirectos en su comunicación. Las críticas se expresan de forma más suavizada y diplomática. Aprende a leer entre líneas: un "eso podría mejorarse" puede significar "esto no está bien". La diplomacia y la cortesía son valores comunicativos fundamentales.',
      },
      {
        subtitle: 'Construcción de relaciones a largo plazo',
        text: 'En Austria, las relaciones profesionales se construyen lentamente y se basan en la confianza mutua. Los austriacos prefieren trabajar con personas que conocen y en quienes confían. Invierte tiempo en construir relaciones genuinas antes de intentar hacer negocios. Un almuerzo o un café puede ser más valioso que diez emails.',
      },
      {
        subtitle: 'El Gemütlichkeit: calidez y comodidad',
        text: 'El concepto austriaco de "Gemütlichkeit" (calidez, comodidad, ambiente agradable) impregna también el entorno laboral. Se valora un ambiente de trabajo agradable, las relaciones cordiales entre colegas y los momentos de distensión. El café vienés, las pausas para el almuerzo y los eventos de equipo son parte importante de la cultura laboral.',
      },
    ],
  },
  {
    id: 4,
    icon: 'ri-medal-line',
    color: 'sky',
    title: 'Calidad, excelencia y sectores clave',
    content: [
      {
        subtitle: 'El estándar austriaco de calidad',
        text: 'Austria es sinónimo de calidad en sectores como la música, la arquitectura, la gastronomía, la ingeniería y el turismo de lujo. En el entorno laboral, este estándar se traduce en atención al detalle, presentaciones impecables y un nivel de exigencia muy alto. "Suficientemente bueno" no es el estándar austriaco.',
      },
      {
        subtitle: 'Sectores con mayor demanda de talento',
        text: 'Los sectores más dinámicos en Austria son: turismo y hostelería de lujo (especialmente en Tirol y Salzburgo), ingeniería mecánica y de precisión, tecnología de la información (Viena es un hub tecnológico creciente), sector financiero y bancario, industria farmacéutica y ciencias de la vida, y energías renovables.',
      },
      {
        subtitle: 'Viena como hub internacional',
        text: 'Viena es sede de numerosas organizaciones internacionales (OPEP, OIEA, ONUDI) y de muchas empresas multinacionales con sede para Europa Central y del Este. Esto crea una demanda constante de profesionales multilingües con experiencia internacional. La ciudad combina una calidad de vida excepcional con oportunidades profesionales de primer nivel.',
      },
    ],
  },
  {
    id: 5,
    icon: 'ri-heart-pulse-line',
    color: 'rose',
    title: 'Sistema social y bienestar',
    content: [
      {
        subtitle: 'Sistema de salud (Krankenversicherung)',
        text: 'Austria tiene un sistema de salud universal de alta calidad. Como empleado, contribuirás automáticamente al seguro médico a través de las cotizaciones sociales. La cobertura es amplia e incluye médico de cabecera, especialistas, hospitalización y medicamentos. No necesitas contratar un seguro privado adicional, aunque muchos profesionales lo hacen para acceder a médicos privados.',
      },
      {
        subtitle: 'Vacaciones y derechos laborales',
        text: 'El mínimo legal de vacaciones en Austria es de 25 días hábiles al año (uno de los más altos de Europa). Tras 25 años de servicio, aumenta a 30 días. Las bajas por enfermedad están bien reguladas. El sistema de convenios colectivos (Kollektivvertrag) es muy fuerte y garantiza condiciones laborales mínimas por sector.',
      },
      {
        subtitle: 'Coste de vida y salarios',
        text: 'Viena es la ciudad más cara de Austria pero más asequible que Zúrich o Múnich. El salario mínimo varía por sector gracias a los convenios colectivos, pero en 2024 el mínimo general es de aproximadamente 1.800€ brutos/mes. Los salarios en tecnología, finanzas e ingeniería son significativamente más altos. El sistema fiscal austriaco es progresivo.',
      },
    ],
  },
  {
    id: 6,
    icon: 'ri-music-2-line',
    color: 'teal',
    title: 'Cultura, vida cotidiana e integración',
    content: [
      {
        subtitle: 'La riqueza cultural austriaca',
        text: 'Austria tiene una de las herencias culturales más ricas del mundo: Mozart, Beethoven, Klimt, Freud, Wittgenstein. Viena es la capital mundial de la música clásica y la ópera. Participar en la vida cultural (conciertos, museos, teatro) es una forma excelente de integrarse y de conectar con colegas austriacos que valoran profundamente su patrimonio cultural.',
      },
      {
        subtitle: 'El alemán austriaco',
        text: 'El alemán austriaco tiene particularidades propias: vocabulario diferente al alemán estándar, pronunciación más suave y expresiones locales. Aunque el alemán estándar es perfectamente comprensible, aprender algunas expresiones austriacas demuestra interés por la cultura local. El dialecto vienés (Wienerisch) es especialmente característico.',
      },
      {
        subtitle: 'Integración social y networking',
        text: 'Los austriacos son inicialmente reservados con los extranjeros, pero una vez que te aceptan en su círculo, la relación es duradera y genuina. El networking en Austria se hace a través de asociaciones profesionales, eventos culturales y cámaras de comercio. La Wirtschaftskammer Österreich (WKO) es una red profesional muy importante.',
      },
    ],
  },
];

const tips = [
  { icon: 'ri-check-double-line', text: 'Usa siempre el tratamiento formal (Sie) hasta que te indiquen lo contrario' },
  { icon: 'ri-check-double-line', text: 'Menciona los títulos académicos en comunicaciones formales' },
  { icon: 'ri-check-double-line', text: 'Aprende alemán estándar (Hochdeutsch) antes de llegar' },
  { icon: 'ri-check-double-line', text: 'Investiga el convenio colectivo de tu sector antes de negociar' },
  { icon: 'ri-check-double-line', text: 'Regístrate en la Meldebehörde en los primeros 3 días de llegada' },
  { icon: 'ri-check-double-line', text: 'Participa en la vida cultural: es clave para la integración' },
];

const colorMap: Record<string, { bg: string; text: string; border: string; light: string }> = {
  purple: { bg: 'bg-violet-600', text: 'text-violet-600', border: 'border-violet-200', light: 'bg-violet-50' },
  amber: { bg: 'bg-amber-600', text: 'text-amber-600', border: 'border-amber-200', light: 'bg-amber-50' },
  emerald: { bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-200', light: 'bg-emerald-50' },
  sky: { bg: 'bg-sky-600', text: 'text-sky-600', border: 'border-sky-200', light: 'bg-sky-50' },
  rose: { bg: 'bg-rose-600', text: 'text-rose-600', border: 'border-rose-200', light: 'bg-rose-50' },
  teal: { bg: 'bg-teal-600', text: 'text-teal-600', border: 'border-teal-200', light: 'bg-teal-50' },
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Claves culturales para trabajar en Austria',
  description: 'Formalidad, calidad, tradición y excelencia profesional en Austria. Guía para integrarse con éxito en el mercado laboral austriaco.',
  url: `${siteUrl}/guia-claves-austria`,
  author: { '@type': 'Organization', name: 'BN ĀGENZIA' },
  publisher: { '@type': 'Organization', name: 'BN ĀGENZIA', url: siteUrl },
  inLanguage: 'es',
  about: { '@type': 'Thing', name: 'Mercado laboral Austria' },
  dateModified: '2025-07-14',
};

export default function GuiaClavesAustriaPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 print:bg-white">
      <SeoHead
        title="Claves culturales para trabajar en Austria – Guía | BN ĀGENZIA"
        description="Formalidad, calidad, tradición y excelencia profesional en Austria. Todo lo que necesitas para integrarte con éxito en el mercado laboral austriaco."
        keywords="trabajar en Austria, cultura laboral austriaca, empleo Austria, mercado laboral Austria, integración profesional Austria"
        canonical="/guia-claves-austria"
        schema={schema}
      />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/perfil?tab=recursos')}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#1A2B4A] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              Volver a recursos
            </button>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-full text-xs font-semibold">
                🇦🇹 Austria
              </span>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A2B4A] text-white text-sm font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-printer-line"></i>
                Imprimir / Guardar PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10 print:py-4 print:px-8">
        <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-2xl p-10 mb-10">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 text-4xl">
              🇦🇹
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-violet-400/20 text-violet-300 border border-violet-400/30 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Guía cultural
                </span>
                <span className="text-white/50 text-xs">6 secciones · Lectura: ~12 min</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
                Claves culturales para trabajar en Austria
              </h1>
              <p className="text-white/80 text-base leading-relaxed max-w-2xl">
                Conoce las particularidades del mercado laboral austriaco: formalidad, calidad, tradición y excelencia profesional en uno de los entornos laborales más refinados de Europa.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-10">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-list-check-2 text-[#1A2B4A]"></i>
            Contenido de la guía
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => {
              const c = colorMap[section.color];
              return (
                <a key={section.id} href={`#seccion-${section.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <span className={`w-7 h-7 ${c.bg} text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                    {section.id}
                  </span>
                  <span className="text-sm text-gray-700 group-hover:text-[#1A2B4A] font-medium transition-colors">
                    {section.title}
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        <div className="space-y-8">
          {sections.map((section) => {
            const c = colorMap[section.color];
            return (
              <div key={section.id} id={`seccion-${section.id}`}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print:break-inside-avoid"
              >
                <div className={`${c.light} ${c.border} border-b px-6 py-5 flex items-center gap-4`}>
                  <div className={`w-11 h-11 ${c.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <i className={`${section.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <span className={`text-xs font-semibold ${c.text} uppercase tracking-wide`}>Sección {section.id}</span>
                    <h2 className="text-lg font-bold text-gray-900 leading-tight">{section.title}</h2>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {section.content.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className={`w-1 rounded-full ${c.bg} flex-shrink-0 mt-1`} style={{ minHeight: '100%' }}></div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1.5">{item.subtitle}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 bg-white rounded-xl border border-gray-200 shadow-sm p-8 print:break-inside-avoid">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 bg-[#1A2B4A] rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-multiple-line text-white text-xl"></i>
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Antes de llegar</span>
              <h2 className="text-lg font-bold text-gray-900">Checklist esencial</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                <i className="ri-check-double-line text-emerald-600 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-gray-700 leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-xl p-8 text-center print:hidden">
          <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-line text-white text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">¿Tienes dudas sobre el mercado austriaco?</h3>
          <p className="text-white/80 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
            Nuestro equipo tiene experiencia directa en el mercado laboral austriaco y puede orientarte de forma personalizada.
          </p>
          <button
            onClick={() => navigate('/#contacto')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#1A2B4A] text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            Contactar con el equipo
          </button>
        </div>

        <div className="mt-8 text-center text-xs text-gray-400 pb-8 print:mt-4">
          <p>Guía elaborada por el equipo de BN Agenzia · Uso exclusivo para candidatos registrados</p>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 1.5cm; }
          body { font-size: 11pt; }
        }
      `}</style>
    </div>
  );
}
